import {
    EControlsOperation,
    type ICameraControls,
    type TControlMouse,
    EControlKey,
} from '@/infrastructure/engine3d/camera/controls/ICameraControls';

import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { MOUSE, type Camera } from 'three';

type TMouseAction = 'LEFT' | 'MIDDLE' | 'RIGHT';
type TKeyAction = 'CONTROL' | 'default' | 'SHIFT';

class CustomOrbitControls extends OrbitControls implements ICameraControls {
    private _mouseActions: {
        [key in TKeyAction]: {
            [action in TMouseAction]?: MOUSE;
        };
    } = {
        default: {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: MOUSE.PAN,
        },
        SHIFT: {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: MOUSE.PAN,
        },
        CONTROL: {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: MOUSE.PAN,
        },
    };

    private static _getMouseAction = (mouse: number | string): TMouseAction => {
        switch (mouse) {
            case 0:
                return 'LEFT';
            case 1:
                return 'MIDDLE';
            default:
                return 'RIGHT';
        }
    };

    private static _getKeyAction(key: string | undefined): TKeyAction {
        switch (key) {
            case EControlKey.CTRL:
                return 'CONTROL';
            case EControlKey.SHIFT:
                return 'SHIFT';
            default:
                return 'default';
        }
    }

    private static _getOperationAction(operation: string): MOUSE | undefined {
        switch (operation) {
            case EControlsOperation.PAN:
                return MOUSE.PAN;
            case EControlsOperation.ROTATE:
                return MOUSE.ROTATE;
            case EControlsOperation.ZOOM:
                return MOUSE.DOLLY;
            default:
                return undefined;
        }
    }

    constructor(object: Camera, domElement: HTMLElement) {
        super(object, domElement);

        window.addEventListener('keydown', this.#onKeyDownCallback);
        window.addEventListener('keyup', this.#onKeyUpCallback);
    }

    #onKeyDownCallback = (event: KeyboardEvent) => {
        const { CONTROL, SHIFT } = this._mouseActions;
        if (event.shiftKey) {
            this.mouseButtons = {
                LEFT: SHIFT.LEFT,
                MIDDLE: SHIFT.MIDDLE,
                RIGHT: SHIFT.RIGHT,
            };
        } else if (event.ctrlKey) {
            this.mouseButtons = {
                LEFT: CONTROL.LEFT,
                MIDDLE: CONTROL.MIDDLE,
                RIGHT: CONTROL.RIGHT,
            };
        }
    };

    #onKeyUpCallback = (event: KeyboardEvent) => {
        if (event.key === 'Shift' || event.key === 'Control') {
            this._updateDefaultMouseActions();
        }
    };

    private _updateDefaultMouseActions() {
        const { LEFT, MIDDLE, RIGHT } = this._mouseActions.default;
        this.mouseButtons = {
            LEFT,
            MIDDLE,
            RIGHT,
        };
    }

    dispose(): void {
        window.removeEventListener('keydown', this.#onKeyDownCallback);
        window.removeEventListener('keyup', this.#onKeyUpCallback);

        super.dispose();
    }

    addMouseAction(operation: EControlsOperation, mouse: TControlMouse, key?: EControlKey | undefined): void {
        const keyAction = CustomOrbitControls._getKeyAction(key);
        const mouseAction = CustomOrbitControls._getMouseAction(mouse);
        const action = CustomOrbitControls._getOperationAction(operation);

        this._mouseActions[keyAction][mouseAction] = action;
        this._updateDefaultMouseActions();
    }

    removeMouseAction(mouse: TControlMouse, key?: EControlKey | undefined): boolean {
        const keyAction = CustomOrbitControls._getKeyAction(key);
        const mouseAction = CustomOrbitControls._getMouseAction(mouse);
        this._mouseActions[keyAction][mouseAction] = undefined;
        this._updateDefaultMouseActions();
        return true;
    }
}
export { CustomOrbitControls };
